/**
 * @description Employee shift schedule view for admin/manager dashboard
 *
 * Key features:
 * - Employee list on left panel
 * - Week calendar view on right
 * - Create shift button (creates schedule-category events)
 * - Visual shift blocks with time ranges
 * - Week navigation (prev/next)
 *
 * @dependencies
 * - eventService: Create/edit schedule events
 * - employeeService: Load employees for display
 * - timeClockService: PTO calendar overlay (approved/taken)
 * - PERMISSIONS: Permission constants
 * - EventSlideout: Reused for shift creation/editing with shiftMode
 *
 * @notes
 * - Shifts are events with category='schedule', event_type='shift'
 * - Assigned to employees via event_assignees
 * - Reuses existing event creation API with category filter
 * - Approved PTO overlays day cells; create/assign hard-blocked server-side on overlap
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import EventSlideout from '../../scheduling/EventSlideout/EventSlideout';
import eventService from '../../../services/api/eventService';
import employeeService from '../../../services/api/employeeService';
import timeClockService from '../../../services/api/timeClockService';
import { format, parseISO } from 'date-fns';

const ShiftScheduleView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasOpenedFromUrl = useRef(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const [shifts, setShifts] = useState([]);
  const [ptoEvents, setPtoEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getWeekDates = () => {
    return days.map((_, i) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      return date;
    });
  };

  const weekDates = getWeekDates();

  // Get week end date (Sunday 23:59:59)
  const getWeekEnd = useCallback(() => {
    const end = new Date(currentWeekStart);
    end.setDate(currentWeekStart.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [currentWeekStart]);

  // Load employees
  const loadEmployees = useCallback(async () => {
    try {
      const response = await employeeService.getDirectory({ status: 'active' });
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  }, []);

  // Load shifts + approved PTO overlay for the current week
  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const weekEnd = getWeekEnd();
      const startIso = currentWeekStart.toISOString();
      const endIso = weekEnd.toISOString();
      const [shiftResponse, ptoResponse] = await Promise.all([
        eventService.getEvents({
          category: 'schedule',
          startDate: startIso,
          endDate: endIso,
        }),
        timeClockService.getPtoCalendar(
          currentWeekStart.toISOString().slice(0, 10),
          weekEnd.toISOString().slice(0, 10)
        ).catch((err) => {
          // Overlay is best-effort — missing pto.view_all should not break the grid
          console.warn('PTO calendar overlay unavailable:', err?.message || err);
          return { ptoEvents: [] };
        }),
      ]);
      setShifts(shiftResponse.events || []);
      setPtoEvents(ptoResponse.ptoEvents || []);
    } catch (error) {
      console.error('Failed to load shifts:', error);
      setShifts([]);
      setPtoEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, getWeekEnd]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const handleCreateShift = useCallback(() => {
    setSelectedShift(null);
    setSlideoutOpen(true);
  }, []);

  /**
   * Open create-shift slideout prefilled for a specific employee + day.
   * Draft has no id so EventSlideout treats it as a new shift.
   */
  const handleCreateShiftForCell = useCallback((employee, date) => {
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(date);
    end.setHours(17, 0, 0, 0);

    setSelectedShift({
      eventType: 'shift',
      category: 'schedule',
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      title: employee ? `${employee.firstName} ${employee.lastName}`.trim() : '',
      // Nest `employee` so EventDetailsTab.buildEventData / AssigneesTab can resolve names
      // without requiring the user to re-select the employee in the dropdown.
      assignees: employee
        ? [{
            employeeId: employee.id,
            employee: {
              id: employee.id,
              firstName: employee.firstName,
              lastName: employee.lastName,
            },
            isOrganizer: false,
            requiresResponse: false,
          }]
        : [],
    });
    setSlideoutOpen(true);
  }, []);

  // Handle openModal=shift from URL - only once per navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openModal') === 'shift' && !hasOpenedFromUrl.current && !slideoutOpen) {
      hasOpenedFromUrl.current = true;
      handleCreateShift();
      // Clean up URL but keep the tab
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('openModal');
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    }
  }, [handleCreateShift, location.search, slideoutOpen, navigate]);

  const navigateWeek = (direction) => {
    setCurrentWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + (direction * 7));
      return next;
    });
  };

  const formatWeekRange = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    const opts = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} — ${end.toLocaleDateString('en-US', opts)}, ${end.getFullYear()}`;
  };

  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    setSlideoutOpen(true);
  };

  const handleSlideoutClose = () => {
    setSlideoutOpen(false);
    setSelectedShift(null);
    // Reset the URL flag when closing so it can open again if needed
    hasOpenedFromUrl.current = false;
  };

  const handleShiftSaved = async (eventData) => {
    try {
      if (eventData.id) {
        // Update existing shift
        await eventService.updateEvent(eventData.id, eventData);
      } else {
        // Create new shift
        await eventService.createEvent(eventData);
      }
      await loadShifts();
      handleSlideoutClose();
    } catch (error) {
      console.error('Failed to save shift:', error);
      throw error; // Re-throw so EventDetailsTab can show error
    }
  };

  const handleShiftDeleted = async () => {
    if (selectedShift?.id) {
      try {
        await eventService.deleteEvent(selectedShift.id);
        await loadShifts();
        handleSlideoutClose();
      } catch (error) {
        console.error('Failed to delete shift:', error);
        throw error;
      }
    }
  };

  // Get shifts for a specific employee on a specific day
  const getShiftsForEmployeeDay = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(shift => {
      // Check if employee is assigned to this shift
      const isAssigned = shift.assignees?.some(a => a.employeeId === employeeId);
      if (!isAssigned) return false;

      // Check if shift falls on this day
      const shiftDate = shift.startTime ? format(parseISO(shift.startTime), 'yyyy-MM-dd') : null;
      return shiftDate === dateStr;
    });
  };

  /** Approved/taken PTO covering this employee+day (overlay) */
  const getPtoForEmployeeDay = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return ptoEvents.filter((pto) => {
      const empId = pto.employee_id || pto.employeeId;
      if (empId !== employeeId) return false;
      const start = String(pto.start_date || pto.startDate).slice(0, 10);
      const end = String(pto.end_date || pto.endDate).slice(0, 10);
      return start <= dateStr && end >= dateStr;
    });
  };

  // Format shift time display
  const formatShiftTime = (shift) => {
    if (!shift.startTime || !shift.endTime) return '';
    const start = format(parseISO(shift.startTime), 'h:mm a');
    const end = format(parseISO(shift.endTime), 'h:mm a');
    return `${start} - ${end}`;
  };

  // Show all employees
  const displayEmployees = employees.length > 0 ? employees : [];

  const handleToday = () => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  return (
    <div className="space-y-4">
      {/* Week Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200 flex-1 sm:flex-none">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <div className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-bold text-gray-900 min-w-[140px] sm:min-w-[200px] text-center">
              {formatWeekRange()}
            </div>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600 hover:text-gray-900"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
          
          <button 
            onClick={handleToday}
            className="px-3 py-2 text-xs sm:text-sm font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Schedule Grid - Desktop (Large Screens) */}
      <div className="hidden xl:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Day Headers */}
          <div className="grid grid-cols-8 border-b border-gray-100 bg-white">
            <div className="px-6 py-5 text-[13px] font-bold text-gray-500 uppercase tracking-widest border-r border-gray-100">
              Employee
            </div>
            {weekDates.map((date, i) => {
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={i}
                  className={`px-2 py-4 text-center border-r border-gray-100 last:border-r-0 ${
                    isToday ? 'bg-orange-50/30' : ''
                  }`}
                >
                  <div className={`text-[11px] font-bold uppercase tracking-widest ${isToday ? 'text-orange-600' : 'text-gray-400'}`}>
                    {days[i]}
                  </div>
                  <div className={`text-lg font-bold mt-1 ${isToday ? 'text-orange-700' : 'text-gray-900'}`}>
                    {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-sm">Loading shifts...</p>
              </div>
            </div>
          )}

          {/* Empty/No Shifts States */}
          {!loading && displayEmployees.length === 0 && (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <div className="text-center">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No employees found</p>
              </div>
            </div>
          )}

          {/* Employee Rows */}
          {!loading && displayEmployees.length > 0 && (
            <div className="divide-y divide-gray-100">
              {displayEmployees.map((employee) => {
                return (
                  <div key={employee.id} className="grid grid-cols-8 group">
                    <div className="px-4 py-5 border-r border-gray-100 bg-white flex items-center gap-4 transition-colors group-hover:bg-gray-50">
                      <div className="min-w-0 flex flex-col">
                        <p className="text-[15px] font-bold text-gray-900 truncate tracking-tight">
                          {employee.firstName} {employee.lastName}
                        </p>
                        {(employee.jobTitle || employee.position) && (
                          <p className="text-[13px] font-medium text-gray-500 truncate mt-0.5">
                            {employee.jobTitle || employee.position}
                          </p>
                        )}
                      </div>
                    </div>

                    {weekDates.map((date, dayIndex) => {
                      const isToday = date.toDateString() === new Date().toDateString();
                      const dayShifts = getShiftsForEmployeeDay(employee.id, date);
                      const dayPto = getPtoForEmployeeDay(employee.id, date);
                      return (
                        <div
                          key={dayIndex}
                          className={`px-2 py-3 min-h-[90px] border-r border-gray-100 last:border-r-0 ${isToday ? 'bg-orange-50/10' : ''} ${dayPto.length > 0 ? 'bg-sky-50/40' : ''}`}
                        >
                          {dayPto.map((pto) => (
                            <div
                              key={pto.id}
                              className="w-full mb-1.5 px-2.5 py-1.5 bg-sky-100/80 border-l-4 border-l-sky-500 rounded-r-lg text-left"
                              title="Approved time off"
                            >
                              <span className="text-[10px] font-bold uppercase text-sky-800 tracking-wide">
                                PTO · {(pto.leave_type || pto.leaveType || 'off').replace('_', ' ')}
                              </span>
                            </div>
                          ))}
                          {dayShifts.map((shift) => (
                            <button
                              key={shift.id}
                              onClick={() => handleShiftClick(shift)}
                              className="w-full mb-1.5 last:mb-0 px-2.5 py-2 bg-orange-50 border-l-4 border-l-orange-500 shadow-sm hover:shadow-md rounded-r-lg text-left transition-all"
                            >
                              <div className="flex items-center gap-1.5 text-[11px] font-bold text-orange-900">
                                <Clock className="w-3 h-3 flex-shrink-0 text-orange-600" />
                                <span className="truncate uppercase">{formatShiftTime(shift)}</span>
                              </div>
                            </button>
                          ))}
                          {dayShifts.length === 0 && dayPto.length === 0 && (
                            <button
                              type="button"
                              onClick={() => handleCreateShiftForCell(employee, date)}
                              className="w-full h-full min-h-[56px] rounded-lg border border-dashed border-transparent hover:border-orange-300 hover:bg-orange-50/40 text-transparent hover:text-orange-600 text-xs font-semibold transition-all"
                              title={`Add shift for ${employee.firstName}`}
                            >
                              + Shift
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile & Tablet (Portrait & Landscape) List View */}
      <div className="xl:hidden space-y-3">
        {loading ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 text-center text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full mx-auto mb-3" />
            <p>Loading schedule...</p>
          </div>
        ) : displayEmployees.length === 0 ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 text-center text-gray-400">
            <p>No employees found</p>
          </div>
        ) : (
          displayEmployees.map((employee) => {
            const employeeShifts = weekDates.flatMap(date => 
              getShiftsForEmployeeDay(employee.id, date).map(shift => ({ ...shift, date }))
            ).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

            const employeePtoDays = weekDates.flatMap((date) =>
              getPtoForEmployeeDay(employee.id, date).map((pto) => ({ ...pto, date }))
            );

            return (
              <div key={employee.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{employee.firstName} {employee.lastName}</p>
                    {(employee.jobTitle || employee.position) && (
                      <p className="text-xs text-gray-500 truncate">{employee.jobTitle || employee.position}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {employeePtoDays.length > 0 && (
                      <div className="bg-sky-100 text-sky-800 px-2 py-1 rounded text-[10px] font-bold uppercase">
                        {employeePtoDays.length} PTO
                      </div>
                    )}
                    <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-[10px] font-bold uppercase">
                      {employeeShifts.length} {employeeShifts.length === 1 ? 'Shift' : 'Shifts'}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCreateShiftForCell(employee, weekDates[0])}
                      className="text-[10px] font-bold uppercase text-brand hover:underline"
                    >
                      + Shift
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {employeePtoDays.map((pto) => (
                    <div
                      key={`${pto.id}-${pto.date.toISOString()}`}
                      className="w-full px-4 py-3 flex items-center gap-3 bg-sky-50/40"
                      title="Approved time off"
                    >
                      <div className="flex flex-col items-center justify-center w-10 h-10 bg-sky-100 rounded-lg border border-sky-200">
                        <span className="text-[10px] font-bold text-sky-700 uppercase">{format(pto.date, 'EEE')}</span>
                        <span className="text-sm font-bold text-sky-900 leading-none">{format(pto.date, 'd')}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-sky-900 uppercase tracking-wide">
                          PTO · {(pto.leave_type || pto.leaveType || 'off').replace('_', ' ')}
                        </p>
                        <p className="text-xs text-sky-700/80">Approved time off</p>
                      </div>
                    </div>
                  ))}
                  {employeeShifts.map((shift) => (
                    <button
                      key={shift.id}
                      onClick={() => handleShiftClick(shift)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col items-center justify-center w-10 h-10 bg-orange-50 rounded-lg border border-orange-100">
                          <span className="text-[10px] font-bold text-orange-600 uppercase">{format(shift.date, 'EEE')}</span>
                          <span className="text-sm font-bold text-orange-900 leading-none">{format(shift.date, 'd')}</span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{formatShiftTime(shift)}</p>
                          {shift.title && <p className="text-xs text-gray-500 truncate">{shift.title}</p>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                  {employeeShifts.length === 0 && employeePtoDays.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-gray-400">
                      No shifts or time off this week
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Event Slideout for Shift Creation/Editing */}
      <EventSlideout
        isOpen={slideoutOpen}
        onClose={handleSlideoutClose}
        event={selectedShift}
        defaultEventType="shift"
        shiftMode={true}
        onSave={handleShiftSaved}
        onDelete={handleShiftDeleted}
      />
    </div>
  );
};

export default ShiftScheduleView;
